import React from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Whitepaper/resource list — Strapi sections.whitepaper-display-section +
// components.whitepaper. A responsive grid of resource cards (image + title)
// each linking to its url. Used by case-studies, data-sheets, whitepapers.
export const whitepaperListSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().optional(),
  items: z
    .array(
      z.object({
        imageUrl: z.string().optional(),
        title: z.string().min(1),
        url: safeUrl,
      }),
    )
    .default([]),
});

type WhitepaperListProps = z.infer<typeof whitepaperListSchema>;

const Section = styled.section`
  max-width: 112rem;
  margin: 0 auto;
  padding: 4rem 1.6rem;

  @media (min-width: 48em) {
    padding: 6rem 1.6rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.4rem;
  font-weight: 400;
  letter-spacing: 5px;
  text-transform: uppercase;

  @media (min-width: 48em) {
    font-size: 1.7rem;
  }
`;

const Heading = styled.h2`
  margin: 0.8rem 0 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 2.6rem;
  max-width: 65rem;

  @media (min-width: 48em) {
    font-size: 4.8rem;
    letter-spacing: -2px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1.6rem;
  row-gap: 4rem;
  margin-top: 3rem;

  @media (min-width: 48em) {
    grid-template-columns: repeat(4, 1fr);
    column-gap: 2.4rem;
    margin-top: 4rem;
  }
`;

const Card = styled.a`
  position: relative;
  display: block;
  height: 15rem;
  border-radius: 2rem;
  overflow: hidden;
  text-decoration: none;

  &:hover .wp-overlay {
    opacity: 1;
  }
  &:hover .wp-title {
    color: #ffffff;
  }

  @media (min-width: 48em) {
    height: 20rem;
  }
`;

const Overlay = styled.span<{ $img?: string }>`
  position: absolute;
  inset: 0;
  border-radius: 2rem;
  background: ${({ $img }) => ($img ? `url(${$img}) center/cover no-repeat` : '#dbe4fb')};
  filter: brightness(70%);
  opacity: 0.5;
  transition: opacity 0.3s ease;
`;

const CardTitle = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  max-width: 20rem;
  text-align: center;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 1.6rem;
  transition: color 0.3s ease;

  @media (min-width: 48em) {
    font-size: 2rem;
  }
`;

function WhitepaperList({ eyebrow, heading, items }: WhitepaperListProps) {
  return (
    <Section>
      {eyebrow || heading ? (
        <Header>
          {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
          {heading ? <Heading>{heading}</Heading> : null}
        </Header>
      ) : null}
      <Grid>
        {items.map((item, i) => (
          <Card key={`${item.title}-${i}`} href={item.url} target="_blank" rel="noreferrer">
            <Overlay className="wp-overlay" $img={item.imageUrl} />
            <CardTitle className="wp-title">{item.title}</CardTitle>
          </Card>
        ))}
      </Grid>
    </Section>
  );
}

export const whitepaperListBlock: BlockDefinition<WhitepaperListProps> = {
  type: 'whitepaperList',
  schema: whitepaperListSchema,
  Renderer: WhitepaperList,
};
